/**
 * ValueFormatConverter - 多选下拉value格式转换工具
 * 
 * 功能：
 * 1. 支持Array → String格式转换 [1,2,3] → "1,2,3"
 * 2. 支持Array → Array<object>格式转换 [1,2,3] → [{name: xx, value: 1}, {name: xx, value: 2}]
 * 3. 支持Array → Array格式保持不变 [1,2,3] → [1,2,3]
 * 4. Vue 2.5.17兼容性，避免使用新语法
 * 
 * @author domiyong
 * @version 1.5.0
 * @created 2025-01-12
 */

/**
 * 输出格式常量定义
 */
export const OUTPUT_FORMATS = {
  STRING: 'string',
  ARRAY: 'array', 
  ARRAY_OBJECT: 'array<object>'
}

/**
 * 默认分隔符配置
 */
export const DEFAULT_SEPARATOR = ','

/**
 * ValueFormatConverter类 - 数据格式转换器
 */
export class ValueFormatConverter {
  constructor(options = {}) {
    // Vue 2.5.17兼容写法，避免使用?.操作符
    this.separator = (options && options.separator) ? options.separator : DEFAULT_SEPARATOR
    this.defaultFormat = (options && options.defaultFormat) ? options.defaultFormat : OUTPUT_FORMATS.STRING
  }

  /**
   * 主要转换方法 - 根据配置转换数据格式
   * @param {Array} sourceData - 源数据数组 [1,2,3]
   * @param {Array} optionList - 选项列表，用于获取name字段 [{id: 1, name: 'A'}, {id: 2, name: 'B'}]
   * @param {string} outputFormat - 输出格式类型 'string' | 'array' | 'array<object>'
   * @param {Object} fieldMapping - 字段映射配置 {idField: 'id', nameField: 'name'}
   * @returns {string|Array|Array<object>} - 转换后的数据
   */
  convertFormat(sourceData, optionList = [], outputFormat = null, fieldMapping = {}) {
    // 参数验证
    if (!Array.isArray(sourceData)) {
      console.warn('[ValueFormatConverter] sourceData must be an array, got:', typeof sourceData)
      return this._getDefaultValue(outputFormat)
    }

    if (sourceData.length === 0) {
      return this._getDefaultValue(outputFormat)
    }

    // 使用传入的格式或默认格式
    const format = outputFormat || this.defaultFormat
    
    // Vue 2.5.17兼容的字段映射
    const idField = (fieldMapping && fieldMapping.idField) ? fieldMapping.idField : 'id'
    const nameField = (fieldMapping && fieldMapping.nameField) ? fieldMapping.nameField : 'name'

    // 根据格式类型进行转换
    switch (format) {
      case OUTPUT_FORMATS.STRING:
        return this._convertToString(sourceData)
        
      case OUTPUT_FORMATS.ARRAY:
        return this._convertToArray(sourceData)
        
      case OUTPUT_FORMATS.ARRAY_OBJECT:
        return this._convertToArrayObject(sourceData, optionList, idField, nameField)
        
      default:
        console.warn('[ValueFormatConverter] Unknown output format:', format, 'using default string format')
        return this._convertToString(sourceData)
    }
  }

  /**
   * 转换为字符串格式
   * @param {Array} sourceData - [1,2,3]
   * @returns {string} - "1,2,3"
   */
  _convertToString(sourceData) {
    try {
      return sourceData.join(this.separator)
    } catch (error) {
      console.error('[ValueFormatConverter] Error converting to string:', error)
      return ''
    }
  }

  /**
   * 转换为数组格式（保持不变）
   * @param {Array} sourceData - [1,2,3]
   * @returns {Array} - [1,2,3]
   */
  _convertToArray(sourceData) {
    // 创建副本，避免引用问题
    return [...sourceData]
  }

  /**
   * 转换为对象数组格式
   * @param {Array} sourceData - [1,2,3]
   * @param {Array} optionList - [{id: 1, name: 'A'}, {id: 2, name: 'B'}]
   * @param {string} idField - id字段名
   * @param {string} nameField - name字段名
   * @returns {Array<object>} - [{name: 'A', value: 1}, {name: 'B', value: 2}]
   */
  _convertToArrayObject(sourceData, optionList, idField, nameField) {
    try {
      const result = []
      
      // Vue 2.5.17兼容的循环处理
      for (let i = 0; i < sourceData.length; i++) {
        const sourceValue = sourceData[i]
        
        // 从选项列表中查找对应的名称
        let matchedOption = null
        for (let j = 0; j < optionList.length; j++) {
          if (optionList[j] && optionList[j][idField] === sourceValue) {
            matchedOption = optionList[j]
            break
          }
        }
        
        // 构建结果对象
        const resultItem = {
          value: sourceValue,
          name: matchedOption ? matchedOption[nameField] : String(sourceValue)
        }
        
        result.push(resultItem)
      }
      
      return result
    } catch (error) {
      console.error('[ValueFormatConverter] Error converting to array object:', error)
      return []
    }
  }

  /**
   * 获取默认值
   * @param {string} format - 格式类型
   * @returns {string|Array} - 对应格式的默认值
   */
  _getDefaultValue(format) {
    switch (format) {
      case OUTPUT_FORMATS.STRING:
        return ''
      case OUTPUT_FORMATS.ARRAY:
      case OUTPUT_FORMATS.ARRAY_OBJECT:
        return []
      default:
        return ''
    }
  }

  /**
   * 验证输出格式是否有效
   * @param {string} format - 要验证的格式
   * @returns {boolean} - 是否为有效格式
   */
  static isValidFormat(format) {
    return Object.values(OUTPUT_FORMATS).includes(format)
  }

  /**
   * 获取所有支持的格式列表
   * @returns {Array<string>} - 格式列表
   */
  static getSupportedFormats() {
    return Object.values(OUTPUT_FORMATS)
  }
}

/**
 * 工厂函数 - 创建转换器实例
 * @param {Object} options - 配置选项
 * @returns {ValueFormatConverter} - 转换器实例
 */
export function createValueFormatConverter(options = {}) {
  return new ValueFormatConverter(options)
}

/**
 * 便捷转换函数 - 直接转换数据
 * @param {Array} sourceData - 源数据
 * @param {Object} config - 转换配置
 * @param {string} config.outputFormat - 输出格式
 * @param {Array} config.optionList - 选项列表  
 * @param {Object} config.fieldMapping - 字段映射
 * @param {string} config.separator - 分隔符
 * @returns {string|Array|Array<object>} - 转换结果
 */
export function convertValueFormat(sourceData, config = {}) {
  const converter = createValueFormatConverter({
    separator: config.separator,
    defaultFormat: config.outputFormat
  })
  
  return converter.convertFormat(
    sourceData,
    config.optionList,
    config.outputFormat,
    config.fieldMapping
  )
}

export default {
  ValueFormatConverter,
  createValueFormatConverter,
  convertValueFormat,
  OUTPUT_FORMATS,
  DEFAULT_SEPARATOR
}